import FolderDeleteIcon from '@mui/icons-material/FolderDelete'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { Button, Grid, Stack, TextField, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import DialogQR from 'components/Dialogs/DialogQR'
import EditInput from 'components/EditInput'
import FloorMap from 'components/Event/FloorMap'
import OpenStressMap from 'components/Map'
import ShowAndReUploadImage from 'components/ShowAndReUploadImage'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'

let timer: any
const initStepStyle = {
  value: '',
  color: '#000',
  fontSize: 20,
}
export default function UpdateCheckpoint() {
  const { id, pid } = useParams<{ id: string; pid: string }>()
  const event = useAppSelector((s) =>
    s.Event.list.find((f) => f.id === Number(id))
  )
  const templates = useAppSelector((s) => s.UI.templates)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const point = event?.checkpoints.find((f) => f.id === Number(pid))
  // state
  const [canEdit, setCanEdit] = useState(false)
  const [step, setStep] = useState(9999)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [positionValues, setPositionValues] = useState<
    {
      value: string
      color: string
      fontSize: number
    }[]
  >([])
  const [state, setState] = useState<{
    value: string
    color: string
    fontSize: number
  }>(initStepStyle)
  const [showQR, setShowQR] = useState(false)
  // ref
  const canvasRef = useRef({} as HTMLCanvasElement)
  const inputPosValueRef = useRef({} as HTMLInputElement)

  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'イベント一覧',
          url: '/event',
        },
        {
          text: 'イベント詳細',
          url: '/event/' + id,
        },
        {
          text: 'チェックポイント詳細',
          url: '/event/' + id + '/update-point/' + pid,
        },
      ])
    )
  }, [dispatch, id, pid])
  useEffect(() => {
    if (point) {
      step >= point.positionValues.length &&
        setPositionValues(point.positionValues)

      if (positionValues) {
        const selectedTemplate = templates.find(
          (f) => f.id === point.templateId
        )
        const img = new Image()
        if (canvasRef.current && selectedTemplate) {
          const ctx = canvasRef.current.getContext('2d')
          img.src = selectedTemplate.qImage
          img.onload = function () {
            canvasRef.current.width = img.width
            canvasRef.current.height = img.height
            if (ctx) {
              ctx.drawImage(img, 0, 0, 360, (360 * img.height) / img.width)
              selectedTemplate.positions.forEach(({ x, y, size }, i) => {
                if (step === i) {
                  ctx.beginPath()
                  ctx.lineWidth = 2
                  ctx.strokeStyle = '#f00'
                  ctx.rect(x - size / 2, y - size / 2, size, size)
                  ctx.stroke()
                } else if (!positionValues[i] || !positionValues[i].value) {
                  ctx.beginPath()
                  ctx.setLineDash([2, 5])
                  ctx.strokeStyle = '#00f'
                  ctx.rect(x - size / 2, y - size / 2, size, size)
                  ctx.stroke()
                }
                if (
                  !positionValues[i] ||
                  (positionValues[i] && !positionValues[i].value)
                ) {
                  ctx.textAlign = 'center'
                  ctx.textBaseline = 'middle'
                  ctx.font = '16px serif'
                  ctx.fillStyle = state.color
                  ctx.fillText('S' + String(i + 1), x, y)
                } else {
                  ctx.textAlign = 'center'
                  ctx.textBaseline = 'middle'
                  if (step === i) {
                    ctx.font = state.fontSize + 'px serif'
                    ctx.fillStyle = state.color
                    ctx.fillText(state.value, x, y)
                  } else {
                    ctx.font = positionValues[i].fontSize + 'px serif'
                    ctx.fillStyle = positionValues[i].color
                    ctx.fillText(positionValues[i].value, x, y)
                  }
                }

                img.remove()
              })
            }
          }
        }
      }
    }
  }, [
    point,
    positionValues,
    state.color,
    state.fontSize,
    state.value,
    step,
    templates,
  ])
  if (!point || !event) return null
  const handleChangeState = async (
    key: keyof typeof state,
    value: string | number
  ) => {
    await new Promise((rs) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        setState({
          ...state,
          [key]: value,
        })
        rs(1)
      }, 200)
    })
  }
  const handleUpdatePoint = async (val: { lat: number; lng: number }) => {
    try {
      await axiosClient.put('/api/events/' + id + '/checkpoint/' + pid, {
        data: JSON.stringify(val),
      })
      setCanEdit(false)
      const msg = 'チェックポイントを修正しました。'
      enqueueSnackbar(msg, {
        variant: 'success',
      })
    } catch (_error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    }
  }
  return (
    <>
      <DialogQR
        open={showQR}
        url={`cp|${id}|${pid}`}
        header={<h4 className="text-center">{event.name}</h4>}
        footer={<h4 className="text-center">{point.name}</h4>}
        handleClose={() => setShowQR(false)}
      />
      <DialogConfirm
        open={deleteConfirm}
        maxWidth="xs"
        loading={loading}
        handleSubmit={async () => {
          try {
            setLoading(true)
            await axiosClient.delete('/api/events/' + id + '/checkpoint/' + pid)
            const msg = 'チェックポイントを削除しました。'
            enqueueSnackbar(msg, {
              variant: 'success',
            })
            navigate('/event/' + id)
          } catch (error) {
            const msg = 'エラーが発生しました。'
            enqueueSnackbar(msg, {
              variant: 'error',
            })
          } finally {
            setLoading(false)
          }
        }}
        title="確認"
        primaryButtonText="削除"
        primaryButtonColor="error"
        handleClose={() => setDeleteConfirm(false)}
      >
        <Typography>チェックポイントを削除しますか？</Typography>
      </DialogConfirm>
      <Grid
        item
        xs={12}
        mt={3}
        display="flex"
        justifyContent={'space-between'}
        alignItems="center"
        width={'100%'}
        gap={2}
      >
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          sx={{ borderBottom: 2, py: 0, flex: 1 }}
        >
          {event.name}
        </Typography>
      </Grid>
      <Box my={2}>
        {event.useGPS ? (
          <OpenStressMap
            positions={event.checkpoints.filter((f) => f.id !== point.id)}
            center={point}
            canEdit={canEdit}
            useEdit={() => setCanEdit(!canEdit)}
            updatePosition={handleUpdatePoint}
            released={event.released}
          />
        ) : (
          <FloorMap
            curPoint={point}
            floors={event.floors}
            positions={event.checkpoints}
            updatePosition={handleUpdatePoint}
            canEdit={canEdit}
            useEdit={() => setCanEdit(!canEdit)}
            released={event.released}
          />
        )}
        <EditInput
          label="チェックポイント名称"
          value={point.name}
          apiEndpoint={'/api/events/' + id + '/checkpoint/' + pid}
          fieldName="name"
        />

        <Grid container spacing={1} mt={2}>
          <Grid item xs={12} md={12} display="flex" justifyContent="center">
            <Box
              component="canvas"
              border={1}
              borderColor="#999"
              ref={canvasRef}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Stack>
                <label htmlFor="select-color">文字色選択</label>
                <input
                  id="select-color"
                  type="color"
                  value={state.color}
                  onChange={(e) => {
                    const color = e.target.value
                    handleChangeState('color', color)
                  }}
                />
              </Stack>
              <Stack>
                <label htmlFor="select-fontSize">文字サイズ選択</label>
                <input
                  id="select-fontSize"
                  type="range"
                  min={10}
                  max={100}
                  style={{
                    maxWidth: 200,
                  }}
                  value={state.fontSize}
                  onChange={(e) => {
                    const fontSize = e.target.value
                    handleChangeState('fontSize', Number(fontSize))
                  }}
                />
              </Stack>
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="center"
            id="position-input"
          >
            <Box maxWidth="sm" my={2}>
              {step >= point.positionValues.length ? (
                <Button
                  disabled={step === 0}
                  variant="contained"
                  color="info"
                  onClick={() => {
                    setStep(0)
                    setState(positionValues[0])
                  }}
                >
                  修正する
                </Button>
              ) : (
                <Stack direction="row">
                  <Button
                    disabled={step === 0}
                    onClick={() => {
                      if (step > 0) {
                        inputPosValueRef.current.querySelector('input')?.focus()
                        setState(positionValues[step - 1])
                        setStep(step - 1)
                      }
                    }}
                  >
                    前へ
                  </Button>
                  <TextField
                    ref={inputPosValueRef}
                    margin="dense"
                    label={`Slot-${step + 1}`}
                    fullWidth
                    variant="outlined"
                    value={state.value}
                    onFocus={() => {
                      const area = document.getElementById('position-input')
                      if (area) area.classList.remove('bg-red-200')
                    }}
                    onChange={(e) => {
                      setState({
                        ...state,
                        value: e.target.value,
                      })
                    }}
                  />
                  <Button
                    disabled={
                      step >= point.positionValues.length || !state.value
                    }
                    onClick={async () => {
                      if (step < point.positionValues.length) {
                        inputPosValueRef.current.querySelector('input')?.focus()
                        const tmp = [...positionValues]
                        tmp[step] = state
                        setPositionValues(tmp)
                        if (!tmp[step + 1]) {
                          setState({ ...state, value: '' })
                        } else {
                          setState(tmp[step + 1])
                        }
                        setStep(step + 1)
                        if (step === point.positionValues.length - 1) {
                          try {
                            let formData = new FormData()
                            formData.append(
                              'data',
                              JSON.stringify({
                                positionValues: tmp,
                              })
                            )
                            await axiosClient.put(
                              '/api/events/' + id + '/checkpoint/' + pid,
                              formData
                            )
                          } catch (_error) {
                            const msg = 'エラーが発生しました。'
                            enqueueSnackbar(msg, {
                              variant: 'error',
                            })
                          }
                        }
                      }
                    }}
                  >
                    {step >= point.positionValues.length - 1 ? '設定' : '次へ'}
                  </Button>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>

        <EditInput
          label="問題文（任意）"
          value={point.quizText}
          apiEndpoint={'/api/events/' + id + '/checkpoint/' + pid}
          fieldName="quizText"
        />
        <EditInput
          label="解答（カンマ区切りで複数設定可能）"
          value={point.quizAnswers}
          apiEndpoint={'/api/events/' + id + '/checkpoint/' + pid}
          fieldName="quizAnswers"
        />
        <EditInput
          label="ヒント（任意）"
          value={point.hint}
          multiline
          rows={4}
          apiEndpoint={'/api/events/' + id + '/checkpoint/' + pid}
          fieldName="hint"
        />
        <Typography fontWeight="bold" mt={2}>
          クリア後
        </Typography>
        <Typography>クリア画像（任意）</Typography>
        <ShowAndReUploadImage
          src={point.answeredImage}
          apiEndpoint={'/api/events/' + id + '/checkpoint/' + pid}
          fieldName="answeredImage"
          canDelete
        />

        <EditInput
          label="説明文（任意）"
          value={point.answeredText}
          multiline
          rows={4}
          apiEndpoint={'/api/events/' + id + '/checkpoint/' + pid}
          fieldName="answeredText"
        />
        <Stack direction={'row'} justifyContent="center">
          <Button
            sx={{
              width: 200,
              mt: 4,
            }}
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => {
              setShowQR(true)
            }}
          >
            <Typography>二次元コード表示</Typography>
            <QrCode2Icon
              sx={{
                ml: 0.5,
              }}
            />
          </Button>
        </Stack>
        {!event.released ? (
          <Stack direction={'row'} justifyContent="center">
            <Button
              sx={{
                width: 200,
                mt: 4,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setDeleteConfirm(true)
              }}
            >
              <Typography>削除</Typography>
              <FolderDeleteIcon
                sx={{
                  ml: 1,
                }}
              />
            </Button>
          </Stack>
        ) : (
          ''
        )}
      </Box>
    </>
  )
}
